import { Option, Period } from '@spike/model';
import { ClientProfileInvoice } from '@spike/client-model';
import { Payment, Refund } from '@spike/payments-model';
import { PaymentStates } from 'components/Payments/Transactions/Categories';
import { Moment } from 'moment-timezone';
import { CARD_SCHEME_IDS, GENERIC_CARD_PAYMENT_IDS } from 'utils/PaymentsUtils';
import { ClientOverviewActivityLogItem } from './UI/Overview/ClientOverviewActivityLog';
import { ClientOverviewInvoice } from './UI/Overview/ClientOverviewInvoicesTable';
import { ClientOverviewTransaction } from './UI/Overview/ClientOverviewTransactionHistory';

type ProfileOverviewActivityLogEntry = {
    date: Moment;
    event: string;
    category: string;
};

const parseMoneyAmount = (value: number | string): number => {
    if (typeof value === 'number') return value;
    return parseFloat(String(value).replace(/[^0-9.-]+/g, ''));
};

export const hasOutstandingBalance = (remaining: number | string): boolean => {
    const amount = parseMoneyAmount(remaining);
    return !Number.isNaN(amount) && amount > 0;
};

export const formatOverviewTagLabel = (tag: string): string =>
    tag
        .replace(/[-_]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/\b\w/g, character => character.toUpperCase());

export const formatOverviewMoney = (value: number | string | undefined): string => {
    const amount = typeof value === 'string' ? parseFloat(value) : value ?? 0;
    if (Number.isNaN(amount)) return '$0.00';
    const prefix = amount < 0 ? '-$' : '$';
    return `${prefix}${Math.abs(amount).toFixed(2)}`;
};

export const formatOverviewShowRate = (value: number | undefined): string => {
    if (value === undefined || Number.isNaN(value)) return '—';
    const percentage = value <= 1 ? value * 100 : value;
    return `${Math.round(percentage)}%`;
};

export const formatOverviewCreatedAt = (date: Moment): string => {
    return `Created ${date.format('D MMM YYYY')}`;
};

export const formatOverviewDateTime = (date: Moment, timeZone: string): string => {
    return date ? date.clone().tz(timeZone).format('MM/DD/YYYY, h:mm a') : '';
};

export const resolveInvoiceNumberFromPayments = (invoiceId: number, payments: Payment[]): number => {
    const payment = payments.find(entry => Number(entry.invoice?.id) === Number(invoiceId));
    return payment?.invoice?.number ?? invoiceId;
};

export const mapProfileInvoiceToOverview = (
    invoice: ClientProfileInvoice,
    timezone: string,
    payments: Payment[] = []
): ClientOverviewInvoice => ({
    id: invoice.invoiceId,
    number: invoice.invoiceNumber ?? resolveInvoiceNumberFromPayments(invoice.invoiceId, payments),
    date: formatOverviewDateTime(invoice.date, timezone),
    total: formatOverviewMoney(invoice.total),
    paid: formatOverviewMoney(invoice.amountPaid),
    remaining: formatOverviewMoney(invoice.remaining),
    canVoid: hasOutstandingBalance(invoice.remaining) && !hasOutstandingBalance(invoice.amountPaid)
});

const CARD_ICON_PATH = '/images/creditCards/';

export const getOverviewCardIconSrc = (paymentMethodId: string): string => {
    const icons: Record<string, string> = {
        visa: 'icon-visa.svg',
        mc: 'icon-mastercard.svg',
        amex: 'icon-amex.svg'
    };

    return CARD_ICON_PATH.concat(icons[paymentMethodId] ?? 'icon-visa.svg');
};

export const isOverviewCardPaymentMethod = (paymentMethodId: string): boolean => {
    const id = paymentMethodId?.toLowerCase() ?? '';
    if (id === 'cash') return false;
    return CARD_SCHEME_IDS.has(id) || GENERIC_CARD_PAYMENT_IDS.has(id);
};

export const isOverviewCardPayment = (paymentMethodId: string, cardDigits: string | null): boolean => {
    if (!cardDigits) return false;

    const id = paymentMethodId?.toLowerCase() ?? '';
    if (id === 'cash') return false;

    return CARD_SCHEME_IDS.has(id) || GENERIC_CARD_PAYMENT_IDS.has(id);
};

const cleanPaymentMethodName = (paymentMethodName: string, schemeId: string, cardDigits: string | null): string => {
    if (!paymentMethodName) {
        if (schemeId === 'visa') return 'Visa';
        if (schemeId === 'mc') return 'Mastercard';
        if (schemeId === 'amex') return 'Amex';
        if (schemeId === 'cash') return 'Cash';
        return '—';
    }

    // Avoid showing the generic label "Credit Card" for card payments.
    // Prefer scheme name when available; otherwise fallback to "Card".
    const normalizedName = paymentMethodName.trim().toLowerCase();
    const normalizedScheme = schemeId?.trim().toLowerCase() ?? '';
    if (normalizedName === 'credit card') {
        if (normalizedScheme === 'visa') return 'Visa';
        if (normalizedScheme === 'mc') return 'Mastercard';
        if (normalizedScheme === 'amex') return 'Amex';
        if (
            GENERIC_CARD_PAYMENT_IDS.has(normalizedScheme) ||
            CARD_SCHEME_IDS.has(normalizedScheme) ||
            Boolean(cardDigits)
        )
            return 'Card';
    }

    if (!cardDigits) return paymentMethodName;

    return (
        paymentMethodName
            .replace(new RegExp(`\\s*([*·.]{2,}\\s*)?${cardDigits}\\s*$`, 'i'), '')
            .replace(/\s*[-–·]\s*$/, '')
            .trim() || paymentMethodName
    );
};

const mapProfileTransactionStatus = (status: string): Option<string> => {
    const normalized = status?.toLowerCase() ?? '';
    const name = status || '—';

    if (normalized.includes('authorized') && normalized.includes('pending')) {
        return { id: PaymentStates.AUTHORISED_PENDING, name };
    }
    if (normalized.includes('authorised') && normalized.includes('pending')) {
        return { id: PaymentStates.AUTHORISED_PENDING, name };
    }
    if (normalized.includes('authorized') || normalized.includes('authorised')) {
        return { id: PaymentStates.AUTHORIZED, name };
    }
    if (normalized.includes('refund')) {
        return { id: PaymentStates.REFUNDED, name };
    }
    if (normalized.includes('cancel')) {
        return { id: PaymentStates.CANCELLED, name };
    }
    if (normalized.includes('error')) {
        return { id: PaymentStates.ERROR, name };
    }
    if (normalized.includes('refused') || normalized.includes('declin')) {
        return { id: PaymentStates.REFUSED, name };
    }
    if (normalized.includes('expired')) {
        return { id: PaymentStates.EXPIRED, name };
    }

    return { id: status, name };
};

export const mapPaymentToOverview = (payment: Payment, timezone: string): ClientOverviewTransaction => {
    const schemeId = payment.paymentMethod?.id ?? '';
    const cardDigits = payment.paymentMethod?.cardDigits ?? null;
    const safePaymentMethodId = schemeId || (cardDigits ? 'card' : schemeId);
    return {
        id: payment.uuid,
        date: formatOverviewDateTime(payment.paidAt, timezone),
        amount: formatOverviewMoney(payment.amount),
        paymentMethodId: safePaymentMethodId,
        paymentMethodName: cleanPaymentMethodName(payment.paymentMethod?.name, schemeId, cardDigits),
        cardDigits,
        status: payment.status
    };
};

const formatRefundAmount = (amount: number | string): string => {
    return formatOverviewMoney(amount);
};

export const mapPaymentRefundToOverview = (
    payment: Payment,
    refund: Refund,
    timezone: string
): ClientOverviewTransaction => {
    const schemeId = payment.paymentMethod?.id ?? '';
    const cardDigits = payment.paymentMethod?.cardDigits ?? null;
    const safePaymentMethodId = schemeId || (cardDigits ? 'card' : schemeId);
    return {
        id: refund.uuid,
        date: formatOverviewDateTime(refund.refundDate, timezone),
        amount: formatRefundAmount(refund.amount),
        paymentMethodId: safePaymentMethodId,
        paymentMethodName: cleanPaymentMethodName(payment.paymentMethod?.name, schemeId, cardDigits),
        cardDigits,
        status: { id: PaymentStates.REFUNDED, name: 'Refunded' },
        isRefund: true
    };
};

export const mapPaymentWithRefundsToOverview = (payment: Payment, timezone: string): ClientOverviewTransaction[] => {
    const base = mapPaymentToOverview(payment, timezone);
    const refunds = (payment.refunds ?? []).map(refund => mapPaymentRefundToOverview(payment, refund, timezone));
    return [base, ...refunds];
};

export const mapActivityLogToOverview = (
    entry: ProfileOverviewActivityLogEntry,
    timezone: string
): ClientOverviewActivityLogItem => ({
    date: formatOverviewDateTime(entry.date, timezone),
    event: entry.event,
    category: entry.category
});

const ACTIVITY_LOG_DATE_FORMAT = 'YYYY/MM/DD';

export type ActivityLogDateParams = {
    dateStart?: string;
    dateEnd?: string;
};

export const getActivityLogDateParams = (period: Period | null): ActivityLogDateParams => {
    if (!period) return {};

    return {
        dateStart: period.from.format(ACTIVITY_LOG_DATE_FORMAT),
        dateEnd: period.to.format(ACTIVITY_LOG_DATE_FORMAT)
    };
};

export const ACTIVITY_LOG_FILTER_CATEGORIES = [
    'Booking',
    'Payment',
    'Invoice',
    'Client',
    'Pet',
    'Membership',
    'Communication',
    'Document'
] as const;

export const getActivityLogCategoryApiValue = (category: string): string => {
    const normalized = category.trim().toLowerCase();

    if (normalized === 'client' || normalized === 'customer') {
        return 'customer';
    }

    return category.trim();
};

export const getActivityLogCategoryDisplayLabel = (category: string): string => {
    if (getActivityLogCategoryApiValue(category) === 'customer') {
        return 'Client';
    }

    const trimmed = category.trim();
    const key = trimmed.toLowerCase();
    const preset = ACTIVITY_LOG_FILTER_CATEGORIES.find(item => item.toLowerCase() === key);

    if (preset) return preset;

    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

export const activityLogCategoriesMatch = (left: string, right: string): boolean =>
    getActivityLogCategoryApiValue(left).toLowerCase() === getActivityLogCategoryApiValue(right).toLowerCase();

export const formatActivityLogCategoryParam = (categories: string[] | undefined): string | undefined => {
    if (!categories?.length) return undefined;

    return categories.map(getActivityLogCategoryApiValue).join(',');
};

export const buildActivityLogCategoryOptions = (activityLogCategories: string[] = []): string[] => {
    const categoriesByKey = new Map<string, string>();

    const addCategory = (category: string) => {
        const trimmed = category.trim();
        if (!trimmed) return;

        const apiValue = getActivityLogCategoryApiValue(trimmed);
        const key = apiValue.toLowerCase();

        if (!categoriesByKey.has(key)) {
            categoriesByKey.set(key, apiValue);
        }
    };

    ACTIVITY_LOG_FILTER_CATEGORIES.forEach(category => addCategory(category === 'Client' ? 'customer' : category));
    activityLogCategories.forEach(addCategory);

    return Array.from(categoriesByKey.values()).sort((a, b) =>
        getActivityLogCategoryDisplayLabel(a).localeCompare(getActivityLogCategoryDisplayLabel(b))
    );
};
